import { defineEvent } from "@visioni/a3s-analytics";
import type { AssetSource, Fleet } from "@visioni/a3s-schema";
import type { CameraApiResponse } from "@src/services/api/cameras-api";
import { getCameraStreamingCapability } from "@src/utils/camera-capabilities";
import { getCameraKeyString, getCameraSourceAndFleet } from "@src/utils/key-parser";
import type { JpegStreamError as JpegStreamErrorCode } from "./hooks/useJpegStream";

/**
 * The camera an event is about. Every camera and livestream event passes it
 * explicitly, although the route context carries the same values on the camera
 * screens: the route has not yet been verified on native, nor for camera → camera
 * navigation (see the 4.1 notes in docs/plans/2026-09-30-a3s-analytics-module.md).
 * An `undefined` value falls back to the route context.
 */
export type CameraScope = {
  tenant_id: string | undefined;
  site_id: string | undefined;
  camera_key: string | undefined;
};

/**
 * The scope for a camera event. An empty id (`useTenantId()` before a tenant is
 * selected, a missing route param) is left to the route context rather than
 * sent as `""`.
 */
export function cameraScope(
  tenantId: string | undefined,
  siteId: string | undefined,
  cameraKey: string | undefined,
): CameraScope {
  return { tenant_id: tenantId || undefined, site_id: siteId || undefined, camera_key: cameraKey || undefined };
}

// Camera lists: the overview list (native), the camera grid and the side panels (web).

/** `source` and `generation` are absent when the camera's asset id cannot be parsed. */
export const CameraClicked = defineEvent<
  CameraScope & { source: AssetSource | undefined; generation: Fleet | undefined }
>("Camera Clicked");

/** Props for `Camera Clicked`, from the camera that was clicked rather than the route. */
export function cameraClickedProps(camera: CameraApiResponse) {
  const sourceAndFleet = getCameraSourceAndFleet(camera);
  return {
    ...cameraScope(camera.site.tenant, camera.site.id, getCameraKeyString(camera)),
    source: sourceAndFleet?.source,
    generation: sourceAndFleet?.fleet,
  };
}

// Camera details: `[cameraKey].native.tsx` and `CameraDetailsContent.web.tsx`.

/** The stream a camera plays: `jpeg` for legacy-app (G1) cameras, `mp4` for the rest. */
export type StreamType = "mp4" | "jpeg";

export function streamTypeOf(camera: CameraApiResponse): StreamType {
  return getCameraStreamingCapability(camera) === "jpeg" ? "jpeg" : "mp4";
}

/**
 * The livestream button on the camera page. `stream_type` is the camera's streaming
 * capability (`"jpeg"` = a G1 / legacy-app camera), not the player that plays it:
 * native opens the JPEG player for those cameras (`JPEG Stream *` events), but web
 * always plays inline (`Livestream *` events). Funnels should follow the event names
 * that come next, not `stream_type`.
 */
export const CameraViewLivestreamClicked = defineEvent<CameraScope & { stream_type: StreamType }>(
  "Camera View Livestream Clicked",
);
export const CameraViewClipsClicked = defineEvent<CameraScope>("Camera View Clips Clicked");
export const CameraViewTimelapseClicked = defineEvent<CameraScope>("Camera View Timelapse Clicked");
export const CameraViewManageAccessClicked = defineEvent<CameraScope>("Camera View Manage Access Clicked");
export const CameraNotificationSettingsClicked = defineEvent<CameraScope>("Camera Notification Settings Clicked");

// Livestream (MP4 / MJPEG): `LivestreamContent` (the livestream route, native and web) and
// `InlineLivestreamPlayer.web.tsx` (inline on the web camera page).

export const LivestreamViewed = defineEvent<CameraScope>("Livestream Viewed");

/**
 * The first frame played. Times are from mount (or the last retry / refresh):
 * `duration_ms` to playing, `camera_data_ready_ms` to the camera loading,
 * `stream_url_ready_ms` to the stream URL, `stream_connect_ms` from the stream
 * URL to playing. A time is `null` when its step was not observed since the
 * last retry.
 */
export const LivestreamStarted = defineEvent<
  CameraScope & {
    duration_ms: number;
    camera_data_ready_ms: number | null;
    stream_url_ready_ms: number | null;
    stream_connect_ms: number | null;
  }
>("Livestream Started");

/**
 * Why a livestream failed. Never the player's error text.
 * - `playback_failed`: the player could not load the stream (the native MJPEG view's
 *   `onError`, or the web iframe's `<video>` / `<img>` error).
 */
export type LivestreamErrorType = "playback_failed";

export const LivestreamError = defineEvent<CameraScope & { error_type: LivestreamErrorType; duration_ms: number }>(
  "Livestream Error",
);
/** Still buffering 15s after the stream URL was set. */
export const LivestreamTimeout = defineEvent<CameraScope & { duration_ms: number }>("Livestream Timeout");

// Still-watching prompt (`useStillWatching`), shared by both livestream players.

export const LivestreamStillWatchingPrompted = defineEvent<CameraScope & { active_ms: number }>(
  "Livestream Still Watching Prompted",
);
export const LivestreamStillWatchingContinued = defineEvent<CameraScope>("Livestream Still Watching Continued");
export const LivestreamStillWatchingStopped = defineEvent<CameraScope & { reason: "auto" | "manual" }>(
  "Livestream Still Watching Stopped",
);

// JPEG stream (legacy-app / G1 cameras): `JpegStreamContent`, on the livestream route.

export const JpegStreamViewed = defineEvent<CameraScope>("JPEG Stream Viewed");
export const JpegStreamStarted = defineEvent<CameraScope>("JPEG Stream Started");
export const JpegStreamError = defineEvent<
  CameraScope & { error_type: Exclude<JpegStreamErrorCode, "timeout_stream"> }
>("JPEG Stream Error");
/** The stream stalled (`timeout_stream`). `error_type` is kept so existing charts continue. */
export const JpegStreamTimeout = defineEvent<CameraScope & { error_type: "timeout_stream" }>("JPEG Stream Timeout");
